import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Grid, 
  Square, 
  Circle, 
  RotateCw, 
  Users, 
  Maximize2, 
  ZoomIn, 
  ZoomOut, 
  MapPin, 
  CheckCircle2, 
  Info, 
  Layers,
  Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/PremiumComponents";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API_URL } from "@/utils/api";
import AdminLayout from "@/components/layouts/AdminLayout";

const TABLE_SHAPES = [
  { id: "square", icon: Square, label: "Cuadrada" },
  { id: "round", icon: Circle, label: "Redonda" },
  { id: "rectangle", icon: Square, label: "Rectangular" },
];

const ZONES = [
  { id: "Salón Principal", color: "bg-blue-500", text: "text-blue-400", border: "border-blue-500/30" },
  { id: "Terraza", color: "bg-orange-500", text: "text-orange-400", border: "border-orange-500/30" },
  { id: "Barra", color: "bg-purple-500", text: "text-purple-400", border: "border-purple-500/30" },
  { id: "VIP", color: "bg-yellow-500", text: "text-yellow-500", border: "border-yellow-500/30" },
];

export default function TableLayoutEditor() {
  const [layout, setLayout] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [activeZone, setActiveZone] = useState("Salón Principal");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const CANVAS_WIDTH = 1200;
  const CANVAS_HEIGHT = 800;

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadActiveLayout();
    }
  }, [selectedBranch]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/branches`, { withCredentials: true });
      const activeBranches = res.data.filter(b => b.active);
      setBranches(activeBranches);
      if (activeBranches.length > 0 && !selectedBranch) {
        setSelectedBranch(activeBranches[0].branch_id);
      }
    } catch (error) {
      toast.error("Error al cargar sucursales");
    }
  };

  const loadActiveLayout = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tables/layouts/active`, {
        headers: { 'X-Branch-Id': selectedBranch },
        withCredentials: true
      });
      setLayout(response.data);
      setTables(response.data.tables || []);
    } catch (error) {
      console.error("No active layout found");
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const addTable = () => {
    const newTable = {
      table_id: `table_${Date.now()}`,
      number: (tables.length + 1).toString(),
      shape: "square",
      x: 100,
      y: 100,
      width: 80,
      height: 80,
      rotation: 0,
      seats: 4,
      status: "available",
      zone: activeZone,
    };
    setTables([...tables, newTable]);
    setSelectedTableId(newTable.table_id);
  };

  const updateTable = (id, updates) => {
    setTables(prev => prev.map(t => t.table_id === id ? { ...t, ...updates } : t));
  };

  const deleteTable = () => {
    if (selectedTableId) {
      setTables(tables.filter(t => t.table_id !== selectedTableId));
      setSelectedTableId(null);
    }
  };

  const saveLayout = async () => {
    try {
      const payload = {
        layout_id: layout?.layout_id !== 'default' ? layout?.layout_id : undefined,
        name: 'Principal',
        is_active: true,
        tables: tables,
        canvas_width: CANVAS_WIDTH,
        canvas_height: CANVAS_HEIGHT
      };

      if (!payload.layout_id) {
        await axios.post(`${API_URL}/tables/layouts`, payload, {
          headers: { 'X-Branch-Id': selectedBranch },
          withCredentials: true
        });
      } else {
        await axios.put(`${API_URL}/tables/layouts/${payload.layout_id}`, payload, {
          headers: { 'X-Branch-Id': selectedBranch },
          withCredentials: true
        });
      }
      toast.success("Distribución guardada correctamente");
      loadActiveLayout();
    } catch (err) {
      toast.error("Error al guardar la distribución");
    }
  };

  const selectedTable = tables.find(t => t.table_id === selectedTableId); // Wait, fix this below

  return (
    <AdminLayout title="Diseño de Sala Premium" noPadding>
      <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-[#050505] font-sans">
        
        {/* SIDEBAR TOOLS */}
        <aside className="w-80 bg-black border-r border-white/5 flex flex-col z-20 shadow-2xl overflow-y-auto scrollbar-hide">
          <div className="p-6 border-b border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent">
            <h2 className="text-xl font-black text-white italic tracking-tighter uppercase mb-1">Editor ZT-TACOS</h2>
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Configuración de mesas y zonas</p>
          </div>

          <div className="p-6 space-y-8">
            {/* SUCURSAL SELECTOR */}
            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 block">Sucursal Activa</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl h-12 focus:ring-yellow-500/20">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-yellow-500" />
                    <SelectValue placeholder="Seleccionar..." />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-800 text-white">
                  {branches.map(b => (
                    <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ACCIONES RÁPIDAS */}
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={addTable} className="bg-yellow-500 hover:bg-yellow-600 text-black font-black uppercase text-xs h-12 rounded-2xl shadow-[0_10px_30px_rgba(234,179,8,0.2)]">
                <Plus className="w-4 h-4 mr-2" /> Mesa
              </Button>
              <Button onClick={saveLayout} variant="outline" className="border-white/10 text-white font-black hover:bg-white/5 uppercase text-xs h-12 rounded-2xl">
                <Save className="w-4 h-4 mr-2" /> Guardar
              </Button>
            </div>

            {/* ZONAS */}
            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-4 block">Filtro por Zona</label>
              <div className="grid grid-cols-2 gap-2">
                {ZONES.map(z => (
                  <button
                    key={z.id}
                    onClick={() => setActiveZone(z.id)}
                    className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                      activeZone === z.id ? "bg-white/5 border-yellow-500/50" : "bg-transparent border-white/5 opacity-40 hover:opacity-100"
                    }`}
                  >
                     <div className={`w-2 h-2 rounded-full ${z.color} mb-1.5`} />
                     <span className="text-[9px] font-black uppercase text-white tracking-tight">{z.id}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* SELECCIÓN ACTUAL */}
            {selectedTableId ? (
              <div className="space-y-6 pt-6 border-t border-white/5 animate-in slide-in-from-bottom-4 duration-300">
                {(() => {
                  const t = tables.find(x => x.table_id === selectedTableId);
                  if (!t) return null;
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-black text-white uppercase italic tracking-tighter">Propiedades Mesa #{t.number}</h3>
                        <Button size="icon" variant="ghost" className="text-red-500 hover:bg-red-500/10 h-8 w-8 rounded-full" onClick={deleteTable}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 block">Número</label>
                          <Input 
                            value={t.number} 
                            onChange={(e) => updateTable(selectedTableId, { number: e.target.value })}
                            className="bg-white/5 border-white/10 text-white rounded-xl h-11"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 mb-2 flex justify-between uppercase">
                            Asientos <span>{t.seats}</span>
                          </label>
                          <input 
                            type="range" min="1" max="12" 
                            value={t.seats}
                            onChange={(e) => updateTable(selectedTableId, { seats: parseInt(e.target.value) })}
                            className="w-full accent-yellow-500 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        <div>
                           <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 block">Forma</label>
                           <div className="grid grid-cols-3 gap-2">
                              {TABLE_SHAPES.map(s => {
                                const Icon = s.icon;
                                const isSel = t.shape === s.id;
                                return (
                                  <button 
                                    key={s.id}
                                    onClick={() => updateTable(selectedTableId, { shape: s.id, width: s.id === "rectangle" ? 120 : 80, height: 80 })}
                                    className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                                      isSel ? "bg-yellow-500 border-yellow-500 text-black" : "bg-white/5 border-white/10 text-gray-500 hover:border-white/20"
                                    }`}
                                  >
                                     <Icon className="w-5 h-5 mb-1" />
                                     <span className="text-[8px] font-black uppercase tracking-tight">{s.label}</span>
                                  </button>
                                )
                              })}
                           </div>
                        </div>
                        <div className="flex gap-2">
                           <Button variant="outline" className="flex-1 bg-white/5 border-white/10 text-[9px] font-black uppercase rounded-xl h-11" onClick={() => updateTable(selectedTableId, { rotation: (t.rotation + 90) % 360 })}>
                             <RotateCw className="w-3 h-3 mr-2" /> Rotar
                           </Button>
                           <Button variant="outline" className="flex-1 bg-white/5 border-white/10 text-[9px] font-black uppercase rounded-xl h-11" onClick={() => updateTable(selectedTableId, { width: t.width + 10, height: t.height + 10 })}>
                             <Maximize2 className="w-3 h-3 mr-2" /> Escala
                           </Button>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 block">Mover a Zona</label>
                          <Select value={t.zone} onValueChange={(val) => updateTable(selectedTableId, { zone: val })}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl h-11">
                               <SelectValue placeholder="Zona..." />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-900 border-gray-800 text-white">
                               {ZONES.map(z => (
                                 <SelectItem key={z.id} value={z.id}>{z.id}</SelectItem>
                               ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="p-8 bg-white/[0.02] rounded-3xl border border-dashed border-white/5 text-center transition-all hover:bg-white/[0.04]">
                 <Grid className="w-10 h-10 text-white/5 mx-auto mb-4" />
                 <p className="text-[10px] text-gray-600 leading-relaxed font-black uppercase tracking-widest">Toca una mesa en el plano para editar sus propiedades premium</p>
              </div>
            )}
          </div>
          
          <div className="mt-auto p-6 border-t border-white/5 bg-black/50 backdrop-blur-md flex items-center justify-between">
             <div className="flex items-center gap-1 bg-white/5 p-1 rounded-xl">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}><ZoomOut className="w-4 h-4" /></Button>
                <div className="text-[10px] font-black text-white w-10 text-center">{Math.round(zoom * 100)}%</div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500" onClick={() => setZoom(z => Math.min(2, z + 0.1))}><ZoomIn className="w-4 h-4" /></Button>
             </div>
             <Button variant="ghost" size="icon" className={`h-10 w-10 rounded-xl transition-all ${showGrid ? "bg-yellow-500 text-black shadow-lg" : "text-gray-600 bg-white/5"}`} onClick={() => setShowGrid(!showGrid)}>
               <Grid className="w-5 h-5" />
             </Button>
          </div>
        </aside>

        {/* PLANO / CANVAS */}
        <main className="flex-1 relative overflow-auto bg-[#0a0a0a] p-10 cursor-crosshair scrollbar-hide">
          <div 
             ref={canvasRef}
             className="relative shadow-[0_0_100px_rgba(0,0,0,0.8)] origin-top-left transition-transform duration-200"
             style={{ 
               width: `${CANVAS_WIDTH}px`, 
               height: `${CANVAS_HEIGHT}px`,
               transform: `scale(${zoom})`,
               backgroundImage: showGrid ? 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)' : 'none',
               backgroundSize: '40px 40px',
               backgroundColor: '#0a0a0a',
             }}
             onClick={() => setSelectedTableId(null)}
          >
            {tables.map(table => {
              const isSelected = selectedTableId === table.table_id;
              const zone = ZONES.find(z => z.id === table.zone) || ZONES[0];
              
              return (
                <div
                  key={table.table_id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", table.table_id);
                    // Hide default drag image for cleaner feel
                    const ghost = document.createElement('div');
                    e.dataTransfer.setDragImage(ghost, 0, 0);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrag={(e) => {
                    if (e.clientX === 0 && e.clientY === 0) return;
                    const rect = canvasRef.current.getBoundingClientRect();
                    const x = (e.clientX - rect.left - (table.width * zoom / 2)) / zoom;
                    const y = (e.clientY - rect.top - (table.height * zoom / 2)) / zoom;
                    
                    // Snap to grid
                    const snapX = showGrid ? Math.round(x / 20) * 20 : x;
                    const snapY = showGrid ? Math.round(y / 20) * 20 : y;
                    
                    updateTable(table.table_id, { x: snapX, y: snapY });
                  }}
                  onClick={(e) => { e.stopPropagation(); setSelectedTableId(table.table_id); }}
                  className={`absolute flex flex-col items-center justify-center transition-all shadow-[0_20px_60px_rgba(0,0,0,0.6)] cursor-move group
                    ${isSelected ? "ring-4 ring-yellow-500 ring-offset-8 ring-offset-[#0a0a0a] z-50" : "hover:scale-[1.02]"}
                    ${table.shape === "round" ? "rounded-full" : "rounded-3xl"}`}
                  style={{
                    left: `${table.x}px`,
                    top: `${table.y}px`,
                    width: `${table.width}px`,
                    height: `${table.height}px`,
                    transform: `rotate(${table.rotation}deg)`,
                    backgroundColor: isSelected ? 'rgba(255,184,0,0.05)' : 'rgba(255,255,255,0.02)',
                    backdropFilter: 'blur(10px)',
                    border: isSelected ? '2px solid white' : '2px solid rgba(255,255,255,0.05)'
                  }}
                >
                  {/* Glass Inner Rim */}
                  <div className={`absolute inset-1.5 border border-white/5 pointer-events-none ${table.shape === "round" ? "rounded-full" : "rounded-2xl"}`} />
                  
                  {/* Table Content */}
                  <span className={`text-2xl font-black text-white italic tracking-tighter drop-shadow-2xl ${isSelected ? "animate-pulse" : ""}`}>#{table.number}</span>
                  <div className="flex items-center gap-1.5 mt-1.5 px-2 py-0.5 bg-black/40 rounded-full border border-white/5">
                     <Users className={`w-2.5 h-2.5 ${zone.text}`} />
                     <span className="text-[9px] font-black text-white/60">{table.seats}</span>
                  </div>

                  {/* Corner Accent */}
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-black ${zone.color} shadow-lg shadow-black/50`} />
                </div>
              );
            })}
          </div>
        </main>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
    </AdminLayout>
  );
}
